import React, { useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Trophy, SoccerBall, ArrowsLeftRight, ShieldCheck, Star, Crown, Users as UsersIcon } from '@phosphor-icons/react';
import { getPlayerStat, FORMATIONS, getPositionCompatibility } from '../utils/gameData';

// ============================================================
// Shared awards panel — drop this under every competition tab.
// Tabs: MVP · Scorers · Assists · Clean Sheets · Young POTY · TOTY
// Top 25 per list (configurable via `topN` prop).
// ============================================================

const computeMvpScore = (p, comp) => {
  const g = getPlayerStat(p, 'goals', comp);
  const a = getPlayerStat(p, 'assists', comp);
  const cs = getPlayerStat(p, 'cleanSheets', comp);
  const mp = Math.max(1, getPlayerStat(p, 'matchesPlayed', comp));
  const yc = getPlayerStat(p, 'yellowCards', comp);
  const rc = getPlayerStat(p, 'redCards', comp);
  // Goals × 3 + Assists × 2 + Clean Sheets (GK only) × 2 − cards
  const gkBonus = p.position === 'GK' ? cs * 2 : 0;
  return (g * 3) + (a * 2) + gkBonus - (yc * 0.4) - (rc * 2) + Math.min(5, mp * 0.1);
};

const PlayerRow = ({ rank, player, metric, valueColor = 'text-white' }) => (
  <div className={`flex items-center gap-2 px-2 py-1.5 ${rank % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`} data-testid={`award-row-${rank}`}>
    <span className={`font-mono text-xs font-bold w-6 ${rank === 1 ? 'text-[#FFD700]' : rank <= 3 ? 'text-[#C0C0C0]' : 'text-[#A0A0A5]'}`}>{rank}</span>
    <span className="nationality-flag text-sm">{player.flag || '🌏'}</span>
    <span className="text-xs text-white flex-1 truncate">{player.name}</span>
    <span className="font-mono text-[10px] text-[#6E6E73] w-10 text-center hidden sm:inline">{player.position}</span>
    <span className="text-[10px] text-[#6E6E73] truncate max-w-[90px]">{player.teamName}</span>
    <span className={`font-mono text-sm font-bold w-10 text-right ${valueColor}`}>{metric}</span>
  </div>
);

const AwardList = ({ label, data, metricFn, valueColor = 'text-[#FFD700]', Icon = Trophy, dataTestId = 'award-list' }) => (
  <div className="card-surface rounded-sm" data-testid={dataTestId}>
    <div className="border-b border-[#2C2C35] px-3 py-2 flex items-center gap-2">
      <Icon size={14} weight="fill" className={valueColor} />
      <h4 className="font-heading font-bold text-white text-xs uppercase tracking-wide">{label}</h4>
      <span className="ml-auto text-[10px] text-[#6E6E73]">Top {data.length}</span>
    </div>
    <ScrollArea className="h-[360px]">
      {data.length === 0 ? (
        <p className="text-[#6E6E73] text-xs text-center p-6">No data yet.</p>
      ) : (
        data.map((p, i) => <PlayerRow key={p.id + '-' + i} rank={i + 1} player={p} metric={metricFn(p)} valueColor={valueColor} />)
      )}
    </ScrollArea>
  </div>
);

// Team of the Year: pick best 11 from a canonical formation (4-3-3 by default).
// Score each player per formation slot using MVP score × positional compatibility.
const pickTeamOfYear = (players, comp, formationName = '4-3-3') => {
  const formation = FORMATIONS.find(f => f.name === formationName) || FORMATIONS[0];
  const used = new Set();
  const chosen = [];
  formation.positions.forEach((slot, idx) => {
    let best = null;
    let bestScore = -Infinity;
    players.forEach(p => {
      if (used.has(p.id)) return;
      const compat = getPositionCompatibility(p.position, slot);
      if (compat < 0.6) return; // reject wildly out-of-position picks
      const mvp = computeMvpScore(p, comp);
      const score = mvp * compat + (p.overall || 70) * 0.1;
      if (score > bestScore) { bestScore = score; best = p; }
    });
    if (best) { used.add(best.id); chosen.push({ slot, slotIdx: idx, player: best }); }
  });
  return chosen;
};

export const CompetitionAwards = ({ teams = [], competition = 'league', topN = 25, formationForTOTY = '4-3-3' }) => {
  const [tab, setTab] = useState('mvp');

  const players = useMemo(() => {
    return teams.flatMap(t => (t.players || []).map(p => ({ ...p, teamName: t.name, teamId: t.id })));
  }, [teams]);

  const hasAnyStats = useMemo(() =>
    players.some(p => getPlayerStat(p, 'matchesPlayed', competition) > 0 || getPlayerStat(p, 'goals', competition) > 0),
  [players, competition]);

  const mvp = useMemo(() =>
    [...players].sort((a, b) => computeMvpScore(b, competition) - computeMvpScore(a, competition)).slice(0, topN),
  [players, competition, topN]);

  const scorers = useMemo(() =>
    [...players].filter(p => getPlayerStat(p, 'goals', competition) > 0)
      .sort((a, b) => getPlayerStat(b, 'goals', competition) - getPlayerStat(a, 'goals', competition))
      .slice(0, topN),
  [players, competition, topN]);

  const assists = useMemo(() =>
    [...players].filter(p => getPlayerStat(p, 'assists', competition) > 0)
      .sort((a, b) => getPlayerStat(b, 'assists', competition) - getPlayerStat(a, 'assists', competition))
      .slice(0, topN),
  [players, competition, topN]);

  const cleanSheets = useMemo(() =>
    [...players].filter(p => p.position === 'GK' && getPlayerStat(p, 'cleanSheets', competition) > 0)
      .sort((a, b) => getPlayerStat(b, 'cleanSheets', competition) - getPlayerStat(a, 'cleanSheets', competition))
      .slice(0, topN),
  [players, competition, topN]);

  const youngPoty = useMemo(() =>
    [...players].filter(p => (p.age || 99) <= 21)
      .sort((a, b) => computeMvpScore(b, competition) - computeMvpScore(a, competition))
      .slice(0, topN),
  [players, competition, topN]);

  const toty = useMemo(() =>
    pickTeamOfYear(players, competition, formationForTOTY),
  [players, competition, formationForTOTY]);

  if (!hasAnyStats) {
    return (
      <div className="card-surface rounded-sm p-8 text-center">
        <Trophy size={48} weight="fill" className="text-[#FFD700]/40 mx-auto mb-2" />
        <p className="text-[#A0A0A5] text-sm">No awards yet — play matches to see MVP race, top scorers, Team of the Year, etc.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="competition-awards">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-3 md:grid-cols-6 mb-3">
          <TabsTrigger value="mvp" data-testid="awards-tab-mvp" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-[10px]">MVP</TabsTrigger>
          <TabsTrigger value="scorers" data-testid="awards-tab-scorers" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase text-[10px]">Scorers</TabsTrigger>
          <TabsTrigger value="assists" data-testid="awards-tab-assists" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white font-heading uppercase text-[10px]">Assists</TabsTrigger>
          <TabsTrigger value="cs" data-testid="awards-tab-cs" className="data-[state=active]:bg-[#32D74B] data-[state=active]:text-black font-heading uppercase text-[10px]">Clean Sheets</TabsTrigger>
          <TabsTrigger value="young" data-testid="awards-tab-young" className="data-[state=active]:bg-[#AF52DE] data-[state=active]:text-white font-heading uppercase text-[10px]">Young POTY</TabsTrigger>
          <TabsTrigger value="toty" data-testid="awards-tab-toty" className="data-[state=active]:bg-[#FF9500] data-[state=active]:text-black font-heading uppercase text-[10px]">TOTY</TabsTrigger>
        </TabsList>

        <TabsContent value="mvp">
          <AwardList label={`MVP Voting (Top ${topN})`} data={mvp} metricFn={(p) => computeMvpScore(p, competition).toFixed(1)} valueColor="text-[#FFD700]" Icon={Crown} dataTestId="awards-list-mvp" />
        </TabsContent>
        <TabsContent value="scorers">
          <AwardList label={`Top Scorers (Top ${topN})`} data={scorers} metricFn={(p) => getPlayerStat(p, 'goals', competition)} valueColor="text-[#FF3B30]" Icon={SoccerBall} dataTestId="awards-list-scorers" />
        </TabsContent>
        <TabsContent value="assists">
          <AwardList label={`Top Assists (Top ${topN})`} data={assists} metricFn={(p) => getPlayerStat(p, 'assists', competition)} valueColor="text-[#007AFF]" Icon={ArrowsLeftRight} dataTestId="awards-list-assists" />
        </TabsContent>
        <TabsContent value="cs">
          <AwardList label={`Clean Sheets (Top ${topN})`} data={cleanSheets} metricFn={(p) => getPlayerStat(p, 'cleanSheets', competition)} valueColor="text-[#32D74B]" Icon={ShieldCheck} dataTestId="awards-list-cs" />
        </TabsContent>
        <TabsContent value="young">
          <AwardList label={`Young Player of the Year · U-21 (Top ${topN})`} data={youngPoty} metricFn={(p) => `${computeMvpScore(p, competition).toFixed(1)} · ${p.age}y`} valueColor="text-[#AF52DE]" Icon={Star} dataTestId="awards-list-young" />
        </TabsContent>
        <TabsContent value="toty">
          <TeamOfTheYear toty={toty} formationName={formationForTOTY} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const TeamOfTheYear = ({ toty, formationName }) => (
  <div className="card-surface rounded-sm" data-testid="awards-toty">
    <div className="border-b border-[#2C2C35] px-3 py-2 flex items-center gap-2">
      <UsersIcon size={14} weight="fill" className="text-[#FF9500]" />
      <h4 className="font-heading font-bold text-white text-xs uppercase tracking-wide">Team of the Year · {formationName}</h4>
    </div>
    {toty.length === 0 ? (
      <p className="text-[#6E6E73] text-xs text-center p-6">Not enough data yet.</p>
    ) : (
      <div className="divide-y divide-[#2C2C35]">
        {toty.map(({ slot, player }, idx) => (
          <div key={idx} className="flex items-center gap-2 px-3 py-2" data-testid={`toty-slot-${idx}`}>
            <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FF9500]/15 text-[#FF9500] w-10 text-center">{slot}</span>
            <span className="nationality-flag text-sm">{player.flag || '🌏'}</span>
            <span className="text-xs text-white flex-1 truncate">{player.name}</span>
            <span className="font-mono text-[10px] text-[#6E6E73] w-8">{player.position}</span>
            <span className="text-[10px] text-[#A0A0A5] truncate max-w-[110px]">{player.teamName}</span>
            <span className="font-mono text-xs font-bold text-[#FFD700] w-10 text-right">{player.overall || 70}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);
